import { useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { Spinner } from "./ui";
import { Login, ChangePassword, AccountSettings } from "../pages/Login";

const NAV: { path: string; label: string; highlight?: boolean }[] = [
  { path: "/", label: "Dashboard" },
  { path: "/products", label: "Products" },
  // Legacy-app comparison views — hidden from the nav (owner directive). The routes still exist
  // (/compare/exact, /compare/ls) so they can be re-enabled later by un-commenting these.
  // { path: "/compare/exact", label: "PBS vs ExactOnline" },
  // { path: "/compare/ls", label: "LS vs ExactOnline" },
  { path: "/reconcile", label: "Reconcile" },
  { path: "/safety-review", label: "Safety review" },
  { path: "/unit-mismatches", label: "Unit mismatches" },
  { path: "/sync", label: "Data Sync" },
  { path: "/activity", label: "Activity log" },
  { path: "/settings", label: "Settings" },
  { path: "/handleiding", label: "📘 Manual", highlight: true },
  { path: "/changelog", label: "🕑 Changelog" },
];

export function Layout({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const utils = trpc.useUtils();
  const me = trpc.auth.me.useQuery();
  const info = trpc.system.info.useQuery();
  // Matches SafetyReview.tsx's own polling cadence — this badge and that page's stat tiles share the
  // same query cache key, so keeping the interval equal means they never visibly disagree or lag.
  const safetyReviewCount = trpc.reconcile.safetyReview.count.useQuery(undefined, { enabled: !!me.data, refetchInterval: 15000 });
  const unitMismatchCount = trpc.reconcile.unitMismatches.count.useQuery(undefined, { enabled: !!me.data, refetchInterval: 60000 });
  const [showChangePw, setShowChangePw] = useState(false);
  const logout = trpc.auth.logout.useMutation({ onSuccess: () => utils.auth.me.invalidate() });
  const setWriteMode = trpc.system.setWriteMode.useMutation({ onSuccess: () => utils.system.info.invalidate() });

  if (me.isLoading) {
    return <div className="flex h-screen items-center justify-center"><Spinner label="Loading…" /></div>;
  }
  if (!me.data) return <Login />;
  if (me.data.mustChangePassword) return <ChangePassword forced />;

  const live = info.data?.writeMode === "live";

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900">
      <aside className="flex w-56 flex-col border-r border-slate-200 bg-white">
        <div className="px-4 py-4 text-base font-bold">PBS Integration</div>
        <nav className="flex-1 space-y-0.5 px-2">
          {NAV.map((n) => {
            const active = n.path === "/" ? location === "/" : location.startsWith(n.path);
            const cls = active
              ? "bg-indigo-600 text-white shadow-sm"
              : n.highlight
                ? "mt-1 bg-amber-50 text-amber-800 ring-1 ring-amber-300 hover:bg-amber-100"
                : "text-slate-600 hover:bg-slate-100";
            const badge = n.path === "/safety-review" ? safetyReviewCount.data?.total : n.path === "/unit-mismatches" ? unitMismatchCount.data : undefined;
            return (
              <Link
                key={n.path}
                href={n.path}
                className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition ${cls}`}
              >
                {n.label}
                {!!badge && (
                  <span className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${active ? "bg-white/25" : "bg-red-100 text-red-700"}`}>
                    {badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="px-3 pb-2">
          <Link
            href="/handleiding"
            title="Open the manual"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
            v{info.data?.version ?? "…"}
          </Link>
          {/* Every stamp in the UI is Amsterdam time, whatever the viewer's own clock says — say so once. */}
          <div className="mt-1.5 text-[10px] text-slate-400" title="All dates and times in this app are shown in Netherlands time (CET/CEST), matching the server and the schedules">
            times in CET/CEST · Amsterdam
          </div>
        </div>
        <div className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
          <div className="truncate font-medium text-slate-600" title={me.data.email}>{me.data.email}</div>
          <div className="text-slate-400">{me.data.role}</div>
          <div className="mt-1.5 flex gap-3">
            <button onClick={() => setShowChangePw(true)} className="font-medium text-indigo-600 hover:underline">Account</button>
            <button onClick={() => logout.mutate()} disabled={logout.isPending} className="font-medium text-red-600 hover:underline">{logout.isPending ? "…" : "Log out"}</button>
          </div>
        </div>
      </aside>

      {showChangePw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowChangePw(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <AccountSettings onDone={() => setShowChangePw(false)} />
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <div className={`flex items-center justify-center gap-3 px-4 py-1.5 text-center text-xs font-semibold text-white ${live ? "bg-red-600" : "bg-blue-600"}`}>
          <span>
            {(info.data?.appEnv ?? "…").toUpperCase()} · writes: {info.data?.writeMode ?? "…"}
            {info.data?.writeMode === "dry-run" && " — nothing is sent to live systems"}
            {live && " — LIVE: changes will be sent to ExactOnline & Lightspeed"}
          </span>
          {me.data.role === "admin" && info.data && (
            <button
              disabled={setWriteMode.isPending}
              onClick={() => {
                const next = live ? "dry-run" : "live";
                const ok = next === "live"
                  ? confirm("⚠ Switch to LIVE writes?\n\nFrom now on, applying a change set (and Post N LIVE) will send real changes to ExactOnline & Lightspeed. Make sure your snapshots/reconcile are clean first.\n\nContinue?")
                  : true;
                if (ok) setWriteMode.mutate({ mode: next });
              }}
              className="rounded-full bg-white/20 px-2.5 py-0.5 font-semibold ring-1 ring-white/40 transition hover:bg-white/30"
              title="Switch between live and dry-run (admin)"
            >
              {setWriteMode.isPending ? "…" : live ? "Switch to dry-run" : "Switch to LIVE"}
            </button>
          )}
        </div>
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}
